import React, { useEffect } from "react";
import { buildCanonicalUrl, buildWebPageStructuredData, siteConfig, type StructuredData } from "@/app/seo/site";
import { useLanguage } from "@/app/language/useLanguage";

interface SeoProps {
    title: string;
    description: string;
    path: string;
    canonicalPath?: string;
    robots?: string;
    imagePath?: string;
    ogType?: "website" | "article";
    structuredData?: StructuredData | StructuredData[];
    includeWebPageSchema?: boolean;
}

function upsertMeta(selector: string, attributes: Record<string, string>): void {
    let element = document.head.querySelector<HTMLMetaElement>(selector);

    if (!element) {
        element = document.createElement("meta");
        document.head.appendChild(element);
    }

    Object.entries(attributes).forEach(([key, value]) => {
        element?.setAttribute(key, value);
    });
}

function upsertLink(rel: string, href: string): void {
    let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);

    if (!element) {
        element = document.createElement("link");
        element.rel = rel;
        document.head.appendChild(element);
    }

    element.href = href;
}

function removeManagedStructuredData(): void {
    document.head.querySelectorAll("script[data-seo-managed='true']").forEach((element) => {
        element.remove();
    });
}

const Seo: React.FC<SeoProps> = ({
    title,
    description,
    path,
    canonicalPath,
    robots = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
    imagePath = siteConfig.defaultImagePath,
    ogType = "website",
    structuredData,
    includeWebPageSchema = true,
}) => {
    const { language } = useLanguage();
    const inLanguage = language === "mi" ? "mi" : "en-NZ";
    useEffect(() => {
        const documentTitle = title === siteConfig.name ? title : `${title} | ${siteConfig.name}`;
        const canonicalUrl = buildCanonicalUrl(canonicalPath ?? path);
        const imageUrl = buildCanonicalUrl(imagePath);
        const schemas = [
            ...(includeWebPageSchema ? [buildWebPageStructuredData(documentTitle, description, canonicalPath ?? path, inLanguage)] : []),
            ...(Array.isArray(structuredData) ? structuredData : structuredData ? [structuredData] : []),
        ];

        document.title = documentTitle;

        upsertMeta('meta[name="description"]', { name: "description", content: description });
        upsertMeta('meta[name="robots"]', { name: "robots", content: robots });
        upsertMeta('meta[property="og:title"]', { property: "og:title", content: documentTitle });
        upsertMeta('meta[property="og:description"]', { property: "og:description", content: description });
        upsertMeta('meta[property="og:type"]', { property: "og:type", content: ogType });
        upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
        upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: siteConfig.name });
        upsertMeta('meta[property="og:locale"]', { property: "og:locale", content: siteConfig.locale });
        upsertMeta('meta[property="og:image"]', { property: "og:image", content: imageUrl });
        upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary" });
        upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: documentTitle });
        upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
        upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: imageUrl });
        upsertLink("canonical", canonicalUrl);

        removeManagedStructuredData();
        schemas.forEach((schema) => {
            const scriptElement = document.createElement("script");
            scriptElement.type = "application/ld+json";
            scriptElement.dataset.seoManaged = "true";
            scriptElement.text = JSON.stringify(schema);
            document.head.appendChild(scriptElement);
        });
    }, [canonicalPath, description, imagePath, includeWebPageSchema, inLanguage, ogType, path, robots, structuredData, title]);

    return null;
};

export default Seo;
